import {
  CHUNK_SIZE,
  createGameWorld,
  type GameWorld,
  getConstructionDefinition,
  getObjectProperties,
  NPC_DEFINITIONS,
  type NpcTypeValue,
  type ObjectTypeValue,
  PawnCommandType,
  TILE_PROPERTIES,
  TILE_SIZE,
} from '@dreamgame/shared';
import { Application, TextureSource } from 'pixi.js';
import { InputManager, type InputState } from '../input/InputManager';
import {
  drainNetworkMessages,
  connect as networkConnect,
  disconnect as networkDisconnect,
  sendCameraUpdate,
  sendConstructionOrder,
  sendInput,
  sendMoveCommand,
  sendPawnCommand,
} from '../network/NetworkClient';
import { IS_SAFARI } from '../platform/browser';
import { CursorType, initCursor, setCursor } from '../render/cursor';
import {
  centerOnPosition,
  createRenderer,
  getCamera,
  initRenderer,
  panCamera,
  render as renderFrame,
  resizeRenderer,
  screenToWorld,
  updateCamera,
  updateRenderer,
  zoomAtPoint,
} from '../render/Renderer';
import { generateActions } from '../state/actions';
import { clearChunks, getChunkObjects, getTileAtWorld, hasChunk } from '../state/chunks';
import { isFightModeEnabled } from '../state/combat';
import { markWelcomeReceived } from '../state/connection';
import {
  cancelPlacement,
  clearConstructionSites,
  deselectConstructionSite,
  endLinePlacement,
  getConstructionSiteAtTile,
  isLinePlacing,
  isPlacingConstruction,
  selectConstructionSite,
  selectedConstruction,
  startLinePlacement,
  updateLinePlacement,
  updatePlacementPreview,
} from '../state/construction';
import {
  type ContextMenuTarget,
  canOpenContextMenu,
  closeContextMenu,
  getObjectAtPosition,
  isContextMenuOpen,
  openContextMenu,
} from '../state/contextMenu';
import { getCorpsesAtTile } from '../state/corpses';
import { getDebugFlag, setPlacementDebug, updatePerformanceMetrics } from '../state/debug';
import { clearEntities, getPawnAtPosition, ownedPawns, playerId } from '../state/entities';
import { clearFogState } from '../state/fogOfWar';
import { isProfilerEnabled, recordFrame, recordSectionTime } from '../state/frameProfiler';
import { getGroundItemsAtTile } from '../state/groundItems';
import { getNpcAtPosition } from '../state/npcs';
import { updateProjectilePositions } from '../state/projectiles';
import {
  clearSelection,
  getSelectedIds,
  isDragSelecting,
  selectedIds,
  onMouseDown as selectionMouseDown,
  onMouseMove as selectionMouseMove,
  onMouseUp as selectionMouseUp,
  selectPawn,
} from '../state/selection';
import { clearTerritories } from '../state/territories';
import { updateSmoothTime } from '../state/time';

// Edge scroll configuration
const EDGE_SCROLL_THRESHOLD = 20; // pixels from edge to trigger scrolling
const EDGE_SCROLL_SPEED = 600; // pixels per second (at zoom 1.0)
const ADMIN_CAMERA_UPDATE_INTERVAL_MS = 100;

// Safari-specific defaults to reduce compositor/GPU pressure and cadence artifacts.
const SAFARI_RENDER_DPR_CAP = 1;
const DEFAULT_RENDER_DPR_CAP = IS_SAFARI ? SAFARI_RENDER_DPR_CAP : Number.POSITIVE_INFINITY;
const USE_DELTA_SNAP = !IS_SAFARI;
const USE_PIXEL_SNAP = !IS_SAFARI;

export const GameMode = {
  PLAYER: 'player',
  ADMIN_VIEWER: 'adminViewer',
} as const;

export type GameModeValue = (typeof GameMode)[keyof typeof GameMode];

const resolveRenderResolution = (): number => {
  const envCapRaw = Number(import.meta.env['VITE_RENDER_DPR_CAP']);
  const envCap = Number.isFinite(envCapRaw) && envCapRaw > 0 ? envCapRaw : DEFAULT_RENDER_DPR_CAP;
  const deviceDpr = window.devicePixelRatio || 1;
  return Math.max(1, Math.min(deviceDpr, envCap));
};

// ============================================================================
// Delta Snapping (aka "jitter fix")
// ============================================================================
// rAF timestamps have micro-jitter (~0.5-1ms). A 16ms vs 17ms delta means the
// camera moves different amounts per frame, perceived as microstutter even at
// locked 60fps. We snap delta to the nearest common refresh-rate interval so
// every frame advances by exactly the same amount. Same technique as Godot's
// "physics jitter fix".
// ============================================================================
const SNAP_THRESHOLD_MS = 2;
const COMMON_FRAME_TIMES = [
  1000 / 60, // 16.667ms
  1000 / 120, // 8.333ms
  1000 / 144, // 6.944ms
  1000 / 240, // 4.167ms
];

const snapDelta = ({ delta }: { delta: number }): number => {
  for (const target of COMMON_FRAME_TIMES) {
    if (Math.abs(delta - target) < SNAP_THRESHOLD_MS) {
      return target;
    }
  }
  return delta;
};

// Game state - mutable to avoid allocations in hot loop
interface GameState {
  world: GameWorld;
  running: boolean;
  lastTime: number;
  wasMouseDown: boolean;
  wasRightMouseDown: boolean;
  lastAdminCameraUpdateMs: number;
  mode: GameModeValue;
}

// ============================================================================
// PERFORMANCE: Mutable State Pattern
// ============================================================================
// We use a single mutable state object instead of immutable updates
// (e.g., `state = { ...state, lastTime: now }`) because:
//
// 1. The game loop runs at 60fps - that's 60 object allocations per second
//    just for the spread operator, plus garbage collection pressure
// 2. Each spread creates a new object that the old one must be GC'd
// 3. GC pauses cause microstutter even when FPS counter shows steady 60
//
// Instead, we mutate properties directly: `state.lastTime = now`
// This is safe here because:
// - State is module-private (not exposed to external code)
// - We control all access patterns
// - No reactive UI depends on reference equality
// ============================================================================
const state: GameState = {
  world: null as unknown as GameWorld,
  running: false,
  lastTime: 0,
  wasMouseDown: false,
  wasRightMouseDown: false,
  lastAdminCameraUpdateMs: 0,
  mode: GameMode.PLAYER,
};
let app: Application;
let input: InputManager;
let canvas: HTMLCanvasElement;

export const createGame = async ({
  canvasElement,
  mode = GameMode.PLAYER,
}: {
  canvasElement: HTMLCanvasElement;
  mode?: GameModeValue;
}): Promise<void> => {
  canvas = canvasElement;

  state.world = createGameWorld();
  state.running = false;
  state.lastTime = 0;
  state.wasMouseDown = false;
  state.wasRightMouseDown = false;
  state.lastAdminCameraUpdateMs = 0;
  state.mode = mode;

  app = new Application();
  input = new InputManager();

  // Set nearest-neighbor filtering as default for all textures.
  // This is a pixel-art game — bilinear filtering causes visible
  // texture blending at tile edges during sub-pixel camera movement,
  // perceived as the world "shaking" when panning.
  TextureSource.defaultOptions.scaleMode = 'nearest';

  await app.init({
    canvas,
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: 0x1a1a2e,
    resolution: resolveRenderResolution(),
    autoDensity: !IS_SAFARI,
    preference: 'webgl',
    powerPreference: 'high-performance',
    antialias: false,
    // Safari exhibits more visible cadence jitter with integer pixel snapping.
    roundPixels: USE_PIXEL_SNAP,
  });

  // Stop PixiJS's built-in ticker. Without this, PixiJS runs its own
  // requestAnimationFrame loop that calls app.render() independently of our
  // game loop. Two competing rAF callbacks execute in unpredictable order
  // within each frame -- when PixiJS renders before our update(), the GPU
  // draws stale scene state, causing visible judder even at solid 60fps.
  // We call app.render() manually at the end of our game loop instead.
  app.ticker.stop();

  const { stage } = createRenderer({
    world: state.world,
    options: {
      showPawns: true,
      showFog: mode !== GameMode.ADMIN_VIEWER,
      showEnvironment: mode !== GameMode.ADMIN_VIEWER,
    },
  });
  app.stage.addChild(stage);

  input.init({ canvas });
  initCursor({ canvas });
  await initRenderer();
};

export const start = (): void => {
  if (state.running) {
    return;
  }

  state.running = true;
  // Seed lastTime via rAF so the first delta is near-zero and vsync-aligned
  requestAnimationFrame((now) => {
    state.lastTime = now;
    requestAnimationFrame(loop);
  });
};

export const stop = (): void => {
  state.running = false;
};

export const resize = ({ width, height }: { width: number; height: number }): void => {
  app.renderer.resize(width, height);
  resizeRenderer({ width, height });
};

const loop = (now: DOMHighResTimeStamp): void => {
  if (!state.running) {
    return;
  }

  const rawDelta = now - state.lastTime;
  state.lastTime = now;
  const delta = USE_DELTA_SNAP ? snapDelta({ delta: rawDelta }) : rawDelta;

  // Process all network messages that arrived since last frame.
  // This keeps deserialization + state updates inside the rAF callback
  // instead of running async between frames (which delays the next rAF).
  drainNetworkMessages();

  const profiling = isProfilerEnabled();
  let _t = 0;

  if (profiling) {
    _t = performance.now();
  }
  update({ delta });
  if (profiling) {
    recordSectionTime({ name: 'update', durationMs: performance.now() - _t });
  }

  if (profiling) {
    _t = performance.now();
  }
  renderFrame();
  if (profiling) {
    recordSectionTime({ name: 'render', durationMs: performance.now() - _t });
  }

  // Submit the scene graph to the GPU. This must happen after all scene
  // graph updates (update + renderFrame) within the same rAF callback so
  // the GPU always draws the current frame's state.
  app.render();

  updatePerformanceMetrics();

  // Record frame time for stutter detection and percentiles
  if (profiling) {
    recordFrame({ frameTimeMs: delta });
  }

  requestAnimationFrame(loop);
};

const update = ({ delta }: { delta: number }): void => {
  // Interpolate smooth game hour for environment rendering
  updateSmoothTime();

  // Update world time
  state.world.time.delta = delta;
  state.world.time.elapsed += delta;

  // Process input
  const inputState = input.getState();

  // Handle zoom input from mouse wheel / touchpad
  handleZoomInput();

  // Handle middle mouse drag for camera panning
  if (inputState.middleMouseDown) {
    panCamera({
      screenDeltaX: inputState.mouseDeltaX,
      screenDeltaY: inputState.mouseDeltaY,
    });
  }

  // Handle edge-of-screen camera scrolling
  handleEdgeScrolling({ inputState, delta });

  // Calculate world mouse position
  const worldPos = screenToWorld({
    screenX: inputState.mouseX,
    screenY: inputState.mouseY,
  });
  inputState.mouseWorldX = worldPos.x;
  inputState.mouseWorldY = worldPos.y;

  if (state.mode === GameMode.ADMIN_VIEWER) {
    setCursor({ cursor: CursorType.DEFAULT });
    state.wasMouseDown = false;
    state.wasRightMouseDown = false;

    updateCamera({ input: inputState, delta });
    maybeSendAdminCameraUpdate();
    updateProjectilePositions({ delta });
    updateRenderer({ delta });
    return;
  }

  updatePlacementDebugInfo({ inputState });

  // Update cursor based on fight mode and hover target
  updateCursor({ worldX: worldPos.x, worldY: worldPos.y });

  // Handle selection input
  handleSelectionInput({ inputState });

  // Update camera
  updateCamera({ input: inputState, delta });

  // Send input to server
  sendInput({ input: inputState, tick: state.world.time.tick });

  // Update projectile positions (client-side interpolation)
  updateProjectilePositions({ delta });

  // Update renderer
  updateRenderer({ delta });
};

const maybeSendAdminCameraUpdate = (): void => {
  const nowMs = state.world.time.elapsed;
  if (nowMs - state.lastAdminCameraUpdateMs < ADMIN_CAMERA_UPDATE_INTERVAL_MS) {
    return;
  }

  state.lastAdminCameraUpdateMs = nowMs;
  const camera = getCamera();
  sendCameraUpdate({
    cameraX: camera.x / TILE_SIZE,
    cameraY: camera.y / TILE_SIZE,
    viewWidth: window.innerWidth / camera.zoom / TILE_SIZE,
    viewHeight: window.innerHeight / camera.zoom / TILE_SIZE,
  });
};

const handleZoomInput = (): void => {
  const wheelDeltas = input.consumeWheelDeltas();
  for (const { deltaY, mouseX, mouseY } of wheelDeltas) {
    zoomAtPoint({
      zoomDelta: deltaY,
      screenX: mouseX,
      screenY: mouseY,
    });
  }
};

const handleEdgeScrolling = ({
  inputState,
  delta,
}: {
  inputState: InputState;
  delta: number;
}): void => {
  const { mouseX, mouseY } = inputState;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const camera = getCamera();

  // Calculate scroll speed adjusted for zoom and delta time
  const speed = (EDGE_SCROLL_SPEED * delta) / 1000 / camera.zoom;

  let deltaX = 0;
  let deltaY = 0;

  // Check horizontal edges
  if (mouseX <= EDGE_SCROLL_THRESHOLD) {
    deltaX = -speed;
  } else if (mouseX >= screenWidth - EDGE_SCROLL_THRESHOLD) {
    deltaX = speed;
  }

  // Check vertical edges
  if (mouseY <= EDGE_SCROLL_THRESHOLD) {
    deltaY = -speed;
  } else if (mouseY >= screenHeight - EDGE_SCROLL_THRESHOLD) {
    deltaY = speed;
  }

  // Apply scroll if any edge is triggered
  if (deltaX !== 0 || deltaY !== 0) {
    panCamera({
      screenDeltaX: -deltaX * camera.zoom,
      screenDeltaY: -deltaY * camera.zoom,
    });
  }
};

const updateCursor = ({ worldX, worldY }: { worldX: number; worldY: number }): void => {
  // Only show attack cursor if fight mode is enabled and we have selection
  if (!isFightModeEnabled.value || selectedIds.value.size === 0) {
    setCursor({ cursor: CursorType.DEFAULT });
    return;
  }

  // Check if hovering over a pawn (not our own)
  const hoveredPawn = getPawnAtPosition({ worldX, worldY });
  if (hoveredPawn && !ownedPawns.value.some((p) => p.networkId === hoveredPawn.networkId)) {
    setCursor({ cursor: CursorType.ATTACK });
    return;
  }

  // Check if hovering over an animal
  const hoveredNpc = getNpcAtPosition({ worldX, worldY });
  if (hoveredNpc) {
    setCursor({ cursor: CursorType.ATTACK });
    return;
  }

  // Default cursor
  setCursor({ cursor: CursorType.DEFAULT });
};

// Handle left mouse down event
const handleLeftMouseDown = ({ inputState }: { inputState: InputState }): void => {
  if (isContextMenuOpen.value) {
    closeContextMenu();
  } else {
    // Suppress drag selection when clicking on a world object or construction site
    const tileX = Math.floor(inputState.mouseWorldX / TILE_SIZE);
    const tileY = Math.floor(inputState.mouseWorldY / TILE_SIZE);
    const hasObjectAtClick = getObjectAtPosition({ tileX, tileY }) !== null;
    const hasSiteAtClick = getConstructionSiteAtTile({ tileX, tileY }) !== null;

    selectionMouseDown({
      worldX: inputState.mouseWorldX,
      worldY: inputState.mouseWorldY,
      screenX: inputState.mouseX,
      screenY: inputState.mouseY,
      suppressDrag: hasObjectAtClick || hasSiteAtClick,
    });
  }
};

// Handle left mouse held (dragging)
const handleLeftMouseDrag = ({ inputState }: { inputState: InputState }): void => {
  if (!isContextMenuOpen.value) {
    selectionMouseMove({
      worldX: inputState.mouseWorldX,
      worldY: inputState.mouseWorldY,
    });
  }
};

// Handle left mouse up event
const handleLeftMouseUp = ({
  inputState,
  isShiftHeld,
}: {
  inputState: InputState;
  isShiftHeld: boolean;
}): void => {
  if (isContextMenuOpen.value) {
    return;
  }

  const tileX = Math.floor(inputState.mouseWorldX / TILE_SIZE);
  const tileY = Math.floor(inputState.mouseWorldY / TILE_SIZE);
  const constructionSite = getConstructionSiteAtTile({ tileX, tileY });

  if (constructionSite && !isDragSelecting.value) {
    selectConstructionSite({ networkId: constructionSite.networkId });
    if (!isShiftHeld) {
      clearSelection();
    }
  } else {
    deselectConstructionSite();
    selectionMouseUp({
      worldX: inputState.mouseWorldX,
      worldY: inputState.mouseWorldY,
      screenX: inputState.mouseX,
      screenY: inputState.mouseY,
      additive: isShiftHeld,
    });
  }
};

const handleSelectionInput = ({ inputState }: { inputState: InputState }): void => {
  const isShiftHeld = (inputState.flags & 64) !== 0;

  if (isPlacingConstruction.value) {
    handleConstructionPlacement({ inputState });
    return;
  }

  if (inputState.mouseDown && !state.wasMouseDown) {
    handleLeftMouseDown({ inputState });
  } else if (inputState.mouseDown && state.wasMouseDown) {
    handleLeftMouseDrag({ inputState });
  } else if (!inputState.mouseDown && state.wasMouseDown) {
    handleLeftMouseUp({ inputState, isShiftHeld });
  }

  if (inputState.rightMouseDown && !state.wasRightMouseDown) {
    handleRightClick({
      screenX: inputState.mouseX,
      screenY: inputState.mouseY,
      worldX: inputState.mouseWorldX,
      worldY: inputState.mouseWorldY,
      isShiftHeld,
    });
  }

  state.wasMouseDown = inputState.mouseDown;
  state.wasRightMouseDown = inputState.rightMouseDown;
};

// Handle line placement for 1x1 structures (walls)
const handleLinePlacement = ({
  inputState,
  tileX,
  tileY,
  definition,
}: {
  inputState: InputState;
  tileX: number;
  tileY: number;
  definition: NonNullable<typeof selectedConstruction.value>;
}): void => {
  // Start line placement on mouse down
  if (inputState.mouseDown && !state.wasMouseDown) {
    startLinePlacement({ tileX, tileY });
  }

  // Update line while dragging
  if (inputState.mouseDown && isLinePlacing.value) {
    updateLinePlacement({
      tileX,
      tileY,
      canPlaceAt: ({ x, y }) => canPlaceConstruction({ tileX: x, tileY: y, definition }),
    });
  }

  // Complete line placement on mouse up
  if (!inputState.mouseDown && state.wasMouseDown && isLinePlacing.value) {
    const tiles = endLinePlacement();
    const selectedPawnIds = getSelectedIds();

    for (const tile of tiles) {
      sendConstructionOrder({
        objectType: definition.objectType,
        tileX: tile.x,
        tileY: tile.y,
        pawnIds: selectedPawnIds,
      });
    }
  }

  // Update single preview when not dragging
  if (!isLinePlacing.value) {
    const isValid = canPlaceConstruction({ tileX, tileY, definition });
    updatePlacementPreview({ tileX, tileY, isValid });
  }
};

// Handle single-tile placement for larger structures
const handleSinglePlacement = ({
  inputState,
  tileX,
  tileY,
  definition,
}: {
  inputState: InputState;
  tileX: number;
  tileY: number;
  definition: NonNullable<typeof selectedConstruction.value>;
}): void => {
  const isValid = canPlaceConstruction({ tileX, tileY, definition });
  updatePlacementPreview({ tileX, tileY, isValid });

  if (inputState.mouseDown && !state.wasMouseDown && isValid) {
    const selectedPawnIds = getSelectedIds();
    sendConstructionOrder({
      objectType: definition.objectType,
      tileX,
      tileY,
      pawnIds: selectedPawnIds,
    });
    cancelPlacement();
  }
};

const handleConstructionPlacement = ({ inputState }: { inputState: InputState }): void => {
  const definition = selectedConstruction.value;
  if (!definition) {
    return;
  }

  const tileX = Math.floor(inputState.mouseWorldX / TILE_SIZE);
  const tileY = Math.floor(inputState.mouseWorldY / TILE_SIZE);
  const isLineCompatible = definition.width === 1 && definition.height === 1;

  // Right click cancels placement
  if (inputState.rightMouseDown && !state.wasRightMouseDown) {
    cancelPlacement();
    state.wasRightMouseDown = inputState.rightMouseDown;
    return;
  }

  if (isLineCompatible) {
    handleLinePlacement({ inputState, tileX, tileY, definition });
  } else {
    handleSinglePlacement({ inputState, tileX, tileY, definition });
  }

  state.wasMouseDown = inputState.mouseDown;
  state.wasRightMouseDown = inputState.rightMouseDown;
};

// Check if a single tile can have construction placed on it
const getBlockingObjectAtTile = ({
  x,
  y,
  objects,
}: {
  x: number;
  y: number;
  objects: ReadonlyArray<{ objectType: number; tileX: number; tileY: number }>;
}): { objectType: number } | null => {
  for (const obj of objects) {
    const objProps = getObjectProperties({ objectType: obj.objectType as ObjectTypeValue });
    if (!objProps.blockMovement) {
      continue;
    }
    const width = objProps.width ?? 1;
    const height = objProps.height ?? 1;
    if (x >= obj.tileX && x < obj.tileX + width && y >= obj.tileY && y < obj.tileY + height) {
      return { objectType: obj.objectType };
    }
  }
  return null;
};

const getPlacementTileInfo = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): {
  chunkX: number;
  chunkY: number;
  hasChunk: boolean;
  tileType: number | null;
  tileWalkable: boolean;
  blockedByObject: boolean;
  blockingObjectType: number | null;
  blockedByConstruction: boolean;
  canPlaceAtTile: boolean;
} => {
  const chunkX = Math.floor(tileX / CHUNK_SIZE);
  const chunkY = Math.floor(tileY / CHUNK_SIZE);
  const hasChunkValue = hasChunk({ x: chunkX, y: chunkY });

  if (!hasChunkValue) {
    return {
      chunkX,
      chunkY,
      hasChunk: false,
      tileType: null,
      tileWalkable: false,
      blockedByObject: false,
      blockingObjectType: null,
      blockedByConstruction: false,
      canPlaceAtTile: false,
    };
  }

  const tileType = getTileAtWorld({ tileX, tileY });
  const tileProps = tileType !== undefined ? TILE_PROPERTIES[tileType] : undefined;
  const tileWalkable = Boolean(tileProps?.walkable);
  const objects = getChunkObjects({ x: chunkX, y: chunkY });
  const blockingObject = getBlockingObjectAtTile({ x: tileX, y: tileY, objects });
  const blockedByConstruction = Boolean(getConstructionSiteAtTile({ tileX, tileY }));
  const canPlaceAtTile = tileWalkable && !blockingObject && !blockedByConstruction;

  return {
    chunkX,
    chunkY,
    hasChunk: true,
    tileType: tileType ?? null,
    tileWalkable,
    blockedByObject: Boolean(blockingObject),
    blockingObjectType: blockingObject?.objectType ?? null,
    blockedByConstruction,
    canPlaceAtTile,
  };
};

const getPlacementReason = ({
  info,
}: {
  info: ReturnType<typeof getPlacementTileInfo>;
}): string | null => {
  if (!info.hasChunk) {
    return 'chunk not loaded';
  }
  if (!info.tileWalkable) {
    return 'tile not walkable';
  }
  if (info.blockedByObject) {
    return `blocked by object ${info.blockingObjectType ?? ''}`.trim();
  }
  if (info.blockedByConstruction) {
    return 'blocked by construction';
  }
  return null;
};

const canPlaceAtTile = ({ x, y }: { x: number; y: number }): boolean => {
  const info = getPlacementTileInfo({ tileX: x, tileY: y });
  return info.canPlaceAtTile;
};

const canPlaceConstruction = ({
  tileX,
  tileY,
  definition,
}: {
  tileX: number;
  tileY: number;
  definition: { width: number; height: number };
}): boolean => {
  for (let dx = 0; dx < definition.width; dx++) {
    for (let dy = 0; dy < definition.height; dy++) {
      if (!canPlaceAtTile({ x: tileX + dx, y: tileY + dy })) {
        return false;
      }
    }
  }
  return true;
};

const checkFootprintCanPlace = ({
  tileX,
  tileY,
  definition,
}: {
  tileX: number;
  tileY: number;
  definition: { width: number; height: number };
}): { canPlace: boolean; reason: string | null } => {
  for (let dx = 0; dx < definition.width; dx++) {
    for (let dy = 0; dy < definition.height; dy++) {
      const info = getPlacementTileInfo({ tileX: tileX + dx, tileY: tileY + dy });
      if (!info.canPlaceAtTile) {
        const reason = getPlacementReason({ info });
        return {
          canPlace: false,
          reason:
            reason !== null ? `(${tileX + dx}, ${tileY + dy}) ${reason}` : 'invalid footprint',
        };
      }
    }
  }
  return { canPlace: true, reason: null };
};

const updatePlacementDebugInfo = ({ inputState }: { inputState: InputState }): void => {
  if (!getDebugFlag({ flag: 'showPlacement' })) {
    setPlacementDebug({ info: null });
    return;
  }

  const tileX = Math.floor(inputState.mouseWorldX / TILE_SIZE);
  const tileY = Math.floor(inputState.mouseWorldY / TILE_SIZE);
  const tileInfo = getPlacementTileInfo({ tileX, tileY });

  let canPlaceFootprint: boolean | null = null;
  let footprintReason: string | null = null;

  const definition = selectedConstruction.value;
  if (definition) {
    const result = checkFootprintCanPlace({ tileX, tileY, definition });
    canPlaceFootprint = result.canPlace;
    footprintReason = result.reason;
  }

  if (footprintReason === null) {
    footprintReason = getPlacementReason({ info: tileInfo });
  }

  setPlacementDebug({
    info: {
      tileX,
      tileY,
      chunkX: tileInfo.chunkX,
      chunkY: tileInfo.chunkY,
      hasChunk: tileInfo.hasChunk,
      tileType: tileInfo.tileType,
      tileWalkable: tileInfo.tileWalkable,
      blockedByObject: tileInfo.blockedByObject,
      blockingObjectType: tileInfo.blockingObjectType,
      blockedByConstruction: tileInfo.blockedByConstruction,
      canPlaceAtTile: tileInfo.canPlaceAtTile,
      canPlaceFootprint,
      footprintReason,
    },
  });
};

// Check if pawn is a downed friendly that shouldn't be attacked
const isDownedFriendlyPawn = ({
  pawn,
}: {
  pawn: ReturnType<typeof getPawnAtPosition>;
}): boolean => {
  return Boolean(
    pawn && pawn.ownerId === playerId.value && pawn.isDowned === 1 && pawn.carriedByNetworkId === 0
  );
};

// Handle fight mode attack or move
const handleFightModeAction = ({
  worldX,
  worldY,
  tileX,
  tileY,
  selectedPawnIds,
  queue,
}: {
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  selectedPawnIds: number[];
  queue: boolean;
}): boolean => {
  const targetPawn = getPawnAtPosition({ worldX, worldY });

  // Skip fight mode for downed friendly pawns (allow context menu)
  if (isDownedFriendlyPawn({ pawn: targetPawn })) {
    return false;
  }

  // Attack enemy pawn
  if (targetPawn && targetPawn.ownerId !== playerId.value) {
    sendPawnCommand({
      pawnIds: selectedPawnIds,
      commandType: PawnCommandType.ATTACK,
      targetX: tileX,
      targetY: tileY,
      targetEntity: targetPawn.networkId,
      queue,
    });
    return true;
  }

  // Attack animal
  const targetNpc = getNpcAtPosition({ worldX, worldY });
  if (targetNpc) {
    sendPawnCommand({
      pawnIds: selectedPawnIds,
      commandType: PawnCommandType.ATTACK,
      targetX: tileX,
      targetY: tileY,
      targetEntity: targetNpc.networkId,
      queue,
    });
    return true;
  }

  // No target - move instead
  sendMoveCommand({ pawnIds: selectedPawnIds, targetX: tileX, targetY: tileY, queue });
  return true;
};

// Try to find and show context menu for construction site
const tryShowConstructionMenu = ({
  screenX,
  screenY,
  tileX,
  tileY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  tileX: number;
  tileY: number;
  isShiftHeld: boolean;
}): boolean => {
  const constructionSite = getConstructionSiteAtTile({ tileX, tileY });
  if (!constructionSite) {
    return false;
  }

  const definition = getConstructionDefinition({
    objectType: constructionSite.objectType as ObjectTypeValue,
  });
  const target: ContextMenuTarget = {
    type: 'construction',
    tileX,
    tileY,
    constructionSite,
    objectName: definition?.name ?? 'Construction',
  };
  openContextMenu({
    screenX,
    screenY,
    target,
    actions: generateActions({ target }),
    isShiftQueued: isShiftHeld,
  });
  return true;
};

// Try to find and show context menu for animal
const tryShowAnimalMenu = ({
  screenX,
  screenY,
  worldX,
  worldY,
  tileX,
  tileY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  isShiftHeld: boolean;
}): boolean => {
  const targetNpc = getNpcAtPosition({ worldX, worldY });
  if (!targetNpc) {
    return false;
  }

  const npcDef = NPC_DEFINITIONS.get(targetNpc.npcType as NpcTypeValue);
  const target: ContextMenuTarget = {
    type: 'animal',
    tileX,
    tileY,
    entityNetworkId: targetNpc.networkId,
    entityName: npcDef?.name ?? 'Animal',
  };
  openContextMenu({
    screenX,
    screenY,
    target,
    actions: generateActions({ target }),
    isShiftQueued: isShiftHeld,
  });
  return true;
};

// Try to find and show context menu for downed pawn
const tryShowPawnMenu = ({
  screenX,
  screenY,
  worldX,
  worldY,
  tileX,
  tileY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  isShiftHeld: boolean;
}): boolean => {
  const targetPawn = getPawnAtPosition({ worldX, worldY });
  if (!targetPawn) {
    return false;
  }

  const canShowDownedPawnActions = targetPawn.isDowned === 1 && targetPawn.carriedByNetworkId === 0;
  const canShowEnemyPawnActions =
    targetPawn.ownerId !== playerId.value && targetPawn.isDowned === 0;
  if (!canShowDownedPawnActions && !canShowEnemyPawnActions) {
    return false;
  }

  const target: ContextMenuTarget = {
    type: 'pawn',
    tileX,
    tileY,
    entityNetworkId: targetPawn.networkId,
    entityName: targetPawn.name ?? 'Colonist',
    pawnData: targetPawn,
  };
  openContextMenu({
    screenX,
    screenY,
    target,
    actions: generateActions({ target }),
    isShiftQueued: isShiftHeld,
  });
  return true;
};

// Try to find and show context menu for ground items
const tryShowGroundItemMenu = ({
  screenX,
  screenY,
  tileX,
  tileY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  tileX: number;
  tileY: number;
  isShiftHeld: boolean;
}): boolean => {
  const groundItemsAtTile = getGroundItemsAtTile({ tileX, tileY });
  if (groundItemsAtTile.length === 0) {
    return false;
  }

  const target: ContextMenuTarget = {
    type: 'groundItem',
    tileX,
    tileY,
    groundItems: groundItemsAtTile,
  };
  openContextMenu({
    screenX,
    screenY,
    target,
    actions: generateActions({ target }),
    isShiftQueued: isShiftHeld,
  });
  return true;
};

// Try to find and show context menu for corpse
const tryShowCorpseMenu = ({
  screenX,
  screenY,
  tileX,
  tileY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  tileX: number;
  tileY: number;
  isShiftHeld: boolean;
}): boolean => {
  const corpsesAtTile = getCorpsesAtTile({ tileX, tileY });
  const corpse = corpsesAtTile[0];
  if (!corpse) {
    return false;
  }

  const target: ContextMenuTarget = {
    type: 'corpse',
    tileX,
    tileY,
    corpse,
    corpseName: corpse.name ?? 'Corpse',
  };
  openContextMenu({
    screenX,
    screenY,
    target,
    actions: generateActions({ target }),
    isShiftQueued: isShiftHeld,
  });
  return true;
};

// Show context menu for object or ground
const showObjectOrGroundMenu = ({
  screenX,
  screenY,
  tileX,
  tileY,
  selectedPawnIds,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  tileX: number;
  tileY: number;
  selectedPawnIds: number[];
  isShiftHeld: boolean;
}): void => {
  const object = getObjectAtPosition({ tileX, tileY });

  const target: ContextMenuTarget = object
    ? {
        type: 'object',
        tileX,
        tileY,
        object,
        objectName: getObjectProperties({ objectType: object.objectType as ObjectTypeValue }).name,
      }
    : { type: 'ground', tileX, tileY };

  const actions = generateActions({ target });
  const meaningfulActions = actions.filter((a) => a.id !== 'cancel');

  if (meaningfulActions.length === 0 && selectedPawnIds.length > 0) {
    sendMoveCommand({
      pawnIds: selectedPawnIds,
      targetX: tileX,
      targetY: tileY,
      queue: isShiftHeld,
    });
    return;
  }

  openContextMenu({ screenX, screenY, target, actions, isShiftQueued: isShiftHeld });
};

const handleRightClick = ({
  screenX,
  screenY,
  worldX,
  worldY,
  isShiftHeld,
}: {
  screenX: number;
  screenY: number;
  worldX: number;
  worldY: number;
  isShiftHeld: boolean;
}): void => {
  if (!canOpenContextMenu()) {
    return;
  }

  const tileX = Math.floor(worldX / TILE_SIZE);
  const tileY = Math.floor(worldY / TILE_SIZE);
  const selectedPawnIds = getSelectedIds();

  // Fight mode: direct attack on right-click
  if (isFightModeEnabled.value && selectedPawnIds.length > 0) {
    const handled = handleFightModeAction({
      worldX,
      worldY,
      tileX,
      tileY,
      selectedPawnIds,
      queue: isShiftHeld,
    });
    if (handled) {
      return;
    }
  }

  // Try various context menu targets in priority order
  if (tryShowConstructionMenu({ screenX, screenY, tileX, tileY, isShiftHeld })) {
    return;
  }
  if (tryShowAnimalMenu({ screenX, screenY, worldX, worldY, tileX, tileY, isShiftHeld })) {
    return;
  }
  if (tryShowPawnMenu({ screenX, screenY, worldX, worldY, tileX, tileY, isShiftHeld })) {
    return;
  }
  if (tryShowGroundItemMenu({ screenX, screenY, tileX, tileY, isShiftHeld })) {
    return;
  }
  if (tryShowCorpseMenu({ screenX, screenY, tileX, tileY, isShiftHeld })) {
    return;
  }

  showObjectOrGroundMenu({ screenX, screenY, tileX, tileY, selectedPawnIds, isShiftHeld });
};

export const connect = ({
  serverUrl,
  playerName,
}: {
  serverUrl: string;
  playerName?: string;
}): void => {
  networkConnect({
    url: serverUrl,
    ...(playerName && { playerName }),
    onConnect: () => {
      console.log('Connected to server');
    },
    onDisconnect: () => {
      console.log('Disconnected from server');
      clearEntities();
      clearChunks();
      clearSelection();
      clearFogState();
    },
    onWelcome: ({ message }) => {
      console.log('Welcome received, player ID:', message.playerId);

      // Center camera on first owned pawn
      const pawns = ownedPawns.value;
      const firstPawn = pawns[0];
      if (firstPawn) {
        centerOnPosition({
          x: firstPawn.x * TILE_SIZE,
          y: firstPawn.y * TILE_SIZE,
        });
        if (state.mode !== GameMode.ADMIN_VIEWER) {
          selectPawn({ networkId: firstPawn.networkId });
        }
      }

      markWelcomeReceived();
    },
  });
};

export const disconnect = (): void => {
  networkDisconnect();
  clearEntities();
  clearChunks();
  clearConstructionSites();
  clearTerritories();
  clearSelection();
  clearFogState();
};
